/* eslint-disable react-hooks/rules-of-hooks */
import {
  MouseEventHandler,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  To,
  LinkProps,
  NavigateProps,
  NavigateFunction,
  NavigateOptions,
  URLSearchParamsInit,
  SetURLSearchParams,
  createSearchParams,
  matchPath,
  Outlet as ReactRouterOutlet,
  Link as ReactRouterLink,
  useLocation as useReactRouterLoction,
  useNavigate as useReactRouterNavigate,
  useParams as useReactRouterParams,
  useSearchParams as useReactRouterSearchParams,
  useMatch as useReactRouterMatch,
} from 'react-router-dom';
import {
  Tab,
  multitabHistoryMap,
  useMultitabReady,
  useMultitabState,
  useTabOperations,
} from './state';
import { useMemoizedFn } from 'ahooks';
import { isURL } from 'utils/is';
import { usePermissionRouteConfig } from 'components/Router/context';
import { OutletContext, MultitabRouterContext, MultitabRouterContextValue } from './context';

export function Outlet() {
  const contextValue = useContext(OutletContext);

  if (!contextValue) {
    return <ReactRouterOutlet />;
  }

  return contextValue.outlet;
}

export interface MultitabRouterProps {
  children: ReactNode;
  tab: Tab;
  params?: object;
}

export function MultitabRouter(props: MultitabRouterProps) {
  const { children, tab, params } = props;

  const value = useMemo<MultitabRouterContextValue>(() => {
    const { path, search, state } = tab;

    return {
      pathname: path,
      search,
      state,
      params: params || {},
    };
  }, [tab, params]);

  return <MultitabRouterContext.Provider value={value}>{children}</MultitabRouterContext.Provider>;
}

export function useLocation() {
  const contextValue = useContext(MultitabRouterContext);

  if (!contextValue) {
    return useReactRouterLoction();
  }

  const { pathname, search, state } = contextValue;

  return {
    pathname,
    search,
    state,
  };
}

function getTo(to: To, pathname: string) {
  if (typeof to === 'string') {
    const [path, search] = (
      to.startsWith('/')
        ? to
        : `${pathname}${to.startsWith('?') || pathname === '/' ? to : `/${to}`}`
    ).split('?');

    return {
      pathname: path,
      search: search ? `?${search}` : search,
    };
  }

  return !to.pathname
    ? { ...to, pathname }
    : to.pathname.startsWith('/')
    ? to
    : { ...to, pathname: `${pathname === '/' ? '' : pathname}/${to.pathname}` };
}

const maintabWhitelist = [
  '/workbench',
  '/conversation',
  '/refund',
  '/review',
  '/todo',
  '/tokopedia',
  '/service',
  '/outofservice',
  '/notRole',
  '/404',
  '/403',
];

const multitabWhitelist = ['/dashboard/todo/detail', '/dashboard/receptionScore/assistant'];

const useIsValidTo = () => {
  const { mainRoutes } = usePermissionRouteConfig();
  return function isValidTo(to: To) {
    const path = typeof to === 'string' ? to : to.pathname || '';
    return (
      mainRoutes?.every((route) => !matchPath(route.path, path)) ||
      maintabWhitelist.some((_) => path.startsWith(_))
    );
  };
};

export function useNavigate() {
  const multitabReady = useMultitabReady();
  const { pathname } = useLocation();
  const nav = useReactRouterNavigate();
  const { addTab, updateTab, updateActiveKey } = useTabOperations();
  const { list } = useMultitabState();
  const isValidTo = useIsValidTo();

  return useMemoizedFn<NavigateFunction>((to: number | To, options?: NavigateOptions) => {
    if (!multitabReady && typeof to !== 'number') {
      nav(to, options);
      return;
    }

    if (typeof to === 'number') {
      const { [pathname]: history = [] } = multitabHistoryMap;
      const targetTab = history.findLast((_) => !_.replace);

      if (targetTab) {
        history.splice(to + history.length);
        return updateTab(pathname, targetTab);
      }
      return nav(to);
    }

    if (typeof to === 'string' && isURL(to)) {
      window.open(to, '_blank');
      return;
    }

    const finalTo = getTo(to, pathname);

    if (isValidTo(finalTo)) {
      updateActiveKey(undefined);
      return nav(finalTo, options);
    }

    const tab = {
      path: finalTo.pathname || '',
      search: finalTo.search || '',
      state: options?.state,
    };

    if (options?.replace) {
      if (isValidTo({ pathname }) || list.every(({ path }) => path !== pathname)) {
        addTab(tab);
      } else {
        updateTab(pathname, tab);
      }
    } else if (
      multitabWhitelist.some((_) => tab.path.startsWith(_)) ||
      (pathname.startsWith('/bootguide') && tab.path.startsWith('/bootguide'))
    ) {
      updateTab(pathname, tab, false);
    } else {
      addTab(tab, pathname);
    }
  });
}

export function Link({ to, replace, state, onClick, ...otherProps }: LinkProps) {
  const nav = useNavigate();

  return (
    <ReactRouterLink
      to={to}
      replace={replace}
      state={state}
      onClick={useMemoizedFn<MouseEventHandler<HTMLAnchorElement>>((e) => {
        e.preventDefault();
        onClick?.(e);
        nav(to, { replace, state });
      })}
      {...otherProps}
    />
  );
}

export function Navigate({ to, replace, state }: NavigateProps) {
  const nav = useNavigate();

  useEffect(() => {
    nav(to, { replace, state });
  }, [nav, to, replace, state]);

  return <></>;
}

export function useParams() {
  const contextValue = useContext(MultitabRouterContext);

  if (contextValue) {
    return contextValue.params;
  }

  return useReactRouterParams();
}

export function useSearchParams(defaultInit?: URLSearchParamsInit) {
  const contextValue = useContext(MultitabRouterContext);

  if (!contextValue) {
    return useReactRouterSearchParams(defaultInit);
  }

  const [defaultSearchParams] = useState(() => createSearchParams(defaultInit));

  const hasSetSearchParamsRef = useRef(false);

  const { search } = useLocation();

  const searchParams = useMemo(() => {
    const searchParams = createSearchParams(search);

    if (hasSetSearchParamsRef.current) {
      return searchParams;
    }

    for (const key of defaultSearchParams.keys()) {
      if (!searchParams.has(key)) {
        defaultSearchParams.getAll(key).forEach((value) => {
          searchParams.append(key, value);
        });
      }
    }

    return searchParams;
  }, [search, defaultSearchParams]);

  const nav = useNavigate();

  const setSearchParams = useCallback<SetURLSearchParams>(
    (nextInit, navigateOptions) => {
      const nextSearchParams = createSearchParams(
        typeof nextInit === 'function' ? nextInit(searchParams) : nextInit,
      );
      nav(`?${nextSearchParams}`, navigateOptions);
      hasSetSearchParamsRef.current = true;
    },
    [searchParams, nav],
  );

  return [searchParams, setSearchParams] as const;
}

export const useMatch: typeof useReactRouterMatch = (pattern) => {
  const contextValue = useContext(MultitabRouterContext);

  if (!contextValue) {
    return useReactRouterMatch(pattern);
  }

  const { pathname } = contextValue;
  return useMemo(() => matchPath(pattern, pathname), [pattern, pathname]);
};

export { useReactRouterLoction };
